import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Flex } from "../../Flex";
import { ModalWrapper } from "../ModalWrapper";
import { ToggleContactModal, ToggleContactSuccessAlert } from '../../../Redux/Actions';
import { InputField, MaskedNumberField, MultiLineTextField } from '../Shared';
import { AppState } from '../../../Redux/Models';
import type { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import { Button } from "../..";
import { SendOutlined } from "@mui/icons-material";
import style from './style.module.scss';
import { SendContactEmailAction } from "../../../Redux/Actions/EmailActions";
import { RecaptchaDisclosure } from "../../RecaptchaDisclosure/RecaptchaDisclosure";
import { isFieldFocus, trackContactError, trackContactSubmitted, trackFormStart } from '../../../Utils/Analytics';

export interface ContactModalProps {
    data: any;
}

export const ContactModal = React.memo((props: ContactModalProps) => {
    const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();
    const isOpen = useSelector((s: AppState) => s.utility.toggleContactModal);
    const defaultValues = {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        message: "",
    };
    const [values, setValues] = useState<{ [key: string]: any }>(
        defaultValues
    );
    const [canSubmit, setCanSubmit] = useState(false);
    // One form_start per opening of the modal, on the first field the visitor focuses.
    const formStarted = useRef(false);
    useEffect(() => {
        if (isOpen) formStarted.current = false;
    }, [isOpen]);
    const handleFormStart = (e: React.FocusEvent) => {
        if (formStarted.current || !isFieldFocus(e)) return;
        formStarted.current = true;
        trackFormStart('contact');
    };
    const requiredValues = ["firstName", "lastName", "email", "phoneNumber", "message"];
    const checkIfCanSubmit = useCallback(() => requiredValues.every((v: string) => values[v]), [requiredValues, values]);

    const updateValues = (name: string, value: any) =>
        setValues({ ...values, [name]: value });


    const onSubmit = async (e: any) => {
        e.preventDefault();
        setCanSubmit(false)
        const sent = await dispatch(SendContactEmailAction(values.firstName, values.lastName, values.email, values.phoneNumber, values.message))
        if (sent) trackContactSubmitted()
        else trackContactError()
        setCanSubmit(true)
        dispatch(ToggleContactModal(false))
        dispatch(ToggleContactSuccessAlert(true))
    };

    useEffect(() => {
        setCanSubmit(checkIfCanSubmit());
    }, [values]);

    return (
        <ModalWrapper
            width="350px"
            isOpen={isOpen}
            label={<Flex className={style.header}>Contact Us</Flex>}
            toggleModal={() => dispatch(ToggleContactModal(false))}
        >
            <Flex
                column
                center
                style={{ padding: '20px' }}
            >
              <div onFocusCapture={handleFormStart} style={{ display: 'contents' }}>
                <InputField
                    onChange={updateValues}
                    type="text"
                    name="firstName"
                    label="First Name"
                />
                <InputField
                    onChange={updateValues}
                    type="text"
                    name="lastName"
                    label="Last Name"
                />
                <InputField
                    onChange={updateValues}
                    type="email"
                    name="email"
                    label="Email"
                />
                <MaskedNumberField
                    onChange={updateValues}
                    name="phoneNumber"
                    label="Phone Number"
                />
                <MultiLineTextField
                    onChange={updateValues}
                    name="message"
                    label="Message"
                />
                <Flex style={{ flex: 1, width: '100%' }} justify="flex-end">
                    <Button
                        className={style.button_submit}
                        IconComponent={SendOutlined}
                        onClick={onSubmit}
                        disabled={!canSubmit}
                    >
                        <strong>Send</strong>
                    </Button>
                </Flex>
                <RecaptchaDisclosure />
              </div>
            </Flex>
        </ModalWrapper>
    );
});
